<template>
  <div class="file" :class="[
    { 'animate__animated animate__zoomOutLeft': isDeleting },
    { 'deleting': wasDeleted },
  ]">
    <Loading v-if="isLoading" />
    <template v-else>

      <div class="file__data file__data--large file__data--editable file__data--name"
        :class="{ 'file__data--readonly': file.isDownloadable }">
        <span @click.self="!file.isDownloadable && (showEditablePopup = true)">
          <transition name="fade">
            <PopupEditer v-if="showEditablePopup && !file.isDownloadable" :selected="file.name"
              @togglePopup="showEditablePopup = $event" @submit="renamePolicyFile($event)" />
          </transition>
          {{ file.file_name }}
        </span>
      </div>


      <div class="file__data file__data--small icon icon__pdf" @click="openPolicyFile()"></div>

      <div class="file__data file__data--medium">
        {{ createdAt }}
      </div>

      <div v-if="showDocumentTypeColumn" class="file__data file__data--medium file__data--document-type"
        :title="documentTypeLabel || '—'">
        {{ documentTypeLabel || '—' }}
      </div>

      <div v-if="showDocumentTypeColumn" class="file__data file__data--small file__data--public">
        <div class="checkbox">
          <input
            type="checkbox"
            :id="`media-public-${props.file.uuid || props.file.id}`"
            :checked="Boolean(props.file.is_public ?? false)"
            class="checkbox__checkbox"
            @change="togglePublicState($event.target.checked)"
          />
          <label :for="`media-public-${props.file.uuid || props.file.id}`" class="checkbox__label"></label>
        </div>
      </div>

      <div class="file__data file__data--medium icon icon__download download" @click="downloadFile()"></div>  

      <div v-if="!file.isDownloadable" class="file__data file__data--small icon icon__delete"
        :class="{ 'icon__delete--active': showDeletePopup }" tabindex="-1" @focus="showDeletePopup = true"
        @focusout="showDeletePopup = false" ref="delete">
        <transition name="fade">
          <div class="delete" v-if="showDeletePopup">
            <div class="delete__text">
              {{ $t('allFilesModal.deleteConfirmation') }}
            </div>
            <div class="delete__buttons">
              <div class="button button--confirm" @click="deletePolicyFile()"></div>
              <div class="button button--cancel" @click="closeDeletePopup()"></div>
            </div>
          </div>
        </transition>
      </div>
      <div v-else class="file__data file__data--small" style="cursor: default;"></div>
    </template>
  </div>
</template>

<script setup>
import { ref, computed} from "vue";
import { useStore } from "vuex";
import moment from "moment";
import PopupEditer from "@/partials/PopupEditer";
import Loading from "@/partials/Loading";

defineOptions({
  name: "File"
});

const props = defineProps({
  file: {
    required: true,
    type: Object,
  },
  updateFiles: {
    required: true,
    type: Function,
  },
});

const store = useStore();

// State
const showEditablePopup = ref(false);  
const isDeleting = ref(false);
const wasDeleted = ref(false);
const isLoading = ref(false);
const showDeletePopup = ref(false);

// Template Ref
const deleteRef = ref(null);

// Computed
// const policyFilesModalIsTermination = computed(() => store.getters.policyFilesModalIsTermination);
// const pendenciesFiles = computed(() => store.getters.pendenciesFiles);
const filesEditMode = computed(() => store.getters.filesEditMode);

const createdAt = computed(() => moment(props.file.created_at).format("DD.MM.YYYY"));

const showDocumentTypeColumn = computed(() => {
  const isPolicyContext = store.getters.showPolicyFilesModal === true;
  const hasPolicyMetadata = !!(
    props.file?.policy_id
    || props.file?.policyId
    || props.file?.document_type
    || props.file?.documentType
    || props.file?.document_type_id
    || props.file?.documentTypeId
    || props.file?.document_type_details
    || props.file?.documentTypeDetails
  );

  return isPolicyContext || hasPolicyMetadata;
});

const documentTypeLabel = computed(() => {
  const documentType = props.file?.document_type
    ?? props.file?.documentType
    ?? props.file?.document_type_details
    ?? props.file?.documentTypeDetails
    ?? null;

  if (documentType && typeof documentType === "object") {
    return documentType.name || documentType.code || documentType.title || "";
  }

  if (typeof documentType === "string" && documentType.trim()) {
    return documentType;
  }

  const documentTypeId = props.file?.document_type_id
    ?? props.file?.documentTypeId
    ?? props.file?.document_type?.id
    ?? null;

  const availableDocumentTypes = store.getters.policyForms?.['policy-document-types']
    || store.getters.policyForms?.['document-types']
    || [];

  if (documentTypeId === null || documentTypeId === undefined) {
    return "";
  }

  const matchedType = availableDocumentTypes.find((type) => Number(type.id) === Number(documentTypeId));
  return matchedType?.name || matchedType?.code || "";
});

// Lifecycle 
// Methods
const closeDeletePopup = () => {
  if (deleteRef.value) {
    deleteRef.value.blur();
  }
  showDeletePopup.value = false;
};

const deletePolicyFile = async () => {
  closeDeletePopup();
  isDeleting.value = true;

  await store.dispatch("deleteFile", props.file.uuid);

  wasDeleted.value = true;
  setTimeout(async () => {
    await props.updateFiles();
  }, 300);
};

const renamePolicyFile = async (name) => {
  isLoading.value = true;
  const mimeTypeParts = props.file.file_name.split(".");
  const mimeType = mimeTypeParts[mimeTypeParts.length - 1];

  await store.dispatch("renameFile", {
    uuid: props.file.uuid,
    name: `${name}.${mimeType}`,
  });

  await props.updateFiles();
  isLoading.value = false;
};

const togglePublicState = async (isPublic) => {
  if (!props.file?.uuid) return;

  await store.dispatch("updateMediaPublicPropertyType", {
    uuid: props.file.uuid,
    isPublic: Boolean(isPublic),
  });

  await props.updateFiles();
};

 
const openPolicyFile = async () => {
  if (
    (filesEditMode.value === 'Offers' || filesEditMode.value === 'Pendencies') &&
    props.file.isDownloadable &&
    props.file.offerId
  ) {
    await store.dispatch("openOfferDocument", props.file.offerId);
  } else {
    await store.dispatch("openFile", props.file);
  }
};

const downloadFile = async () => {
  if (
    (filesEditMode.value === 'Offers' || filesEditMode.value === 'Pendencies') &&
    props.file.isDownloadable &&
    props.file.offerId
  ) {
    await store.dispatch("downloadOfferDocument", props.file.offerId);
  } else {
    await store.dispatch("downloadFile", props.file);
  }
};
</script>

<style lang="scss" scoped>
.file__data {
  position: relative;

  &--editable {
    span {
      position: relative;
      cursor: pointer;
    }
  }

  &--readonly {
    span {
      cursor: default !important;
    }
  }

  &--name {
    word-break: break-all;
  }
}

.download {
  height: 28px !important;
  width: 28px !important;
  transform: scale(0.8) !important;
}

.delete {
  position: absolute;
  z-index: 3;
  background: var(--bg-1);
  right: calc(100% + 5px);
  top: 50%;
  transform: translateY(-50%);
  width: 320px;
  display: flex;
  align-items: center;
  border-radius: var(--radius-1);
  border: 1px solid var(--border-2);
  padding: 10px 20px;
  box-shadow: 0px 3px 6px var(--shadow-7);

  &__text {
    line-height: 20px;
  }

  &__buttons {
    display: flex;
  }

  .button {
    width: 24px;
    height: 24px;
    cursor: pointer;
    transition: all 0.3s ease;

    &--confirm {
      background: url("../../assets/icons/default/confirm_icon.svg");
      margin: 0 15px;

      &:hover {
        background: url("../../assets/icons/default/confirm_hover_icon.svg");
      }
    }

    &--cancel {
      background: url("../../assets/icons/default/close_icon.svg");
      box-shadow: none;

      &:hover {
        background: url("../../assets/icons/default/close_hover_icon.svg");
      }
    }
  }
}

.files .file__data {
  justify-content: center !important;
}

.files .file__data--name {
  justify-content: flex-start !important;
}

.file__data {
  background-position: center !important;
}
</style>